import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ExternalLink, ArrowLeft, ChevronRight, Instagram, Globe } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { EVENT_ORGANIZERS, organizerSlug, type EventOrganizerItem } from './EventOrganizer';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&h=800&fit=crop';

// Custom "About" copy for specific listings, applied whenever the card's own
// `description` field is empty. Keyed by lowercase name so this works whether
// the listing is a code default or an admin-added (custom) item.
const FORCE_ABOUT_BY_NAME: Record<string, string> = {
  'dallas creative house':
    "DCH is a community that exists as a reminder that creativity lives in everyone, it just needs the right home to grow. Through collaboration, workshops, and creative events, we bring together artists, entrepreneurs, and everyday creators across Dallas to connect, experiment, and create work that feels authentic and purposeful. We seek to elevate, empower, and connect the creative community in Dallas, bridging the gap between emerging talent and opportunity.",
};

export default function EventProductionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('event_organizers', EVENT_ORGANIZERS, (org, i) =>
    slugify(org.name || String(i))
  );

  const entry: EventOrganizerItem | undefined = mc.items.find(
    (org: any) => organizerSlug(org.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Listing Not Found | Dallas Desi Scoop"
          description="This marketing & production listing could not be found."
          canonical="/event-production"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Listing not found.</p>
        <Link
          to="/event-production"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Marketing & Production
        </Link>
      </div>
    );
  }

  const rawAddress = (entry.address || '').trim();
  const hasRealAddress = rawAddress !== '' && rawAddress !== 'Address coming soon';
  const city = entry.city || (hasRealAddress ? rawAddress.split(',').slice(-2, -1)[0]?.trim() : '') || 'DFW';
  const title = `${entry.name} in ${city} | Marketing & Production | Dallas Desi Scoop`;
  const description = `${entry.name} offers marketing and production services for the South Asian community across Dallas–Fort Worth. Find contact info and social links.`;
  const canonicalPath = `/event-production/${slug}`;

  const rawPhone = (entry.phone || '').trim();
  const isRealPhone = rawPhone !== '' && rawPhone.toLowerCase() !== 'coming soon' && /\d/.test(rawPhone);
  const hasAnySocial = countSocialLinks(entry) > 0;

  const mapsUrl = hasRealAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${entry.name}, ${rawAddress}`)}`
    : '';

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: 'Marketing and production services',
      address: hasRealAddress
        ? {
            '@type': 'PostalAddress',
            addressLocality: city,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      telephone: isRealPhone ? rawPhone : undefined,
      url: entry.contactUrl || entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Marketing & Production', item: `${BASE_URL}/event-production` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/event-production"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Marketing & Production
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {hasRealAddress && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {rawAddress}
              </span>
            )}
            {hasRealAddress && <span className="text-white/40">·</span>}
            <span className="text-white/75 text-[13px]">Marketing & Production</span>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {FORCE_ABOUT_BY_NAME[(entry.name || '').trim().toLowerCase()] || (entry.description || '').trim() || (
              <>
                {entry.name} offers marketing and production services for the South Asian community across Dallas–Fort Worth
                {city && city !== 'DFW' ? `, based in ${city}` : ''}.
                {hasRealAddress ? ` Located at ${rawAddress}.` : ''}
              </>
            )}
          </p>
          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={20} />
            </div>
          )}
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {hasRealAddress && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{rawAddress}</span>
              </div>
            )}
            {isRealPhone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${rawPhone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {rawPhone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          <div className="mt-5 flex flex-col gap-2">
            {entry.contactUrl && (
              <a
                href={entry.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </a>
            )}
            {!entry.contactUrl && isRealPhone && (
              <a
                href={`tel:${rawPhone.replace(/[^\d+]/g, '')}`}
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Get Directions
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
